import React, { createContext, useContext } from 'react';
import { toast } from 'react-toastify';
import { leadsAPI, callsAPI } from '../services/api';
import { useQueryClient } from '@tanstack/react-query';

const AppContext = createContext();

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const queryClient = useQueryClient();

  // Lead Management
  const addLead = async (lead) => {
    try {
      const response = await leadsAPI.createLead(lead);
      // setLeads(prevLeads => [...prevLeads, response.data]);
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add lead');
      throw error;
    }
  };

  const addLeads = async (leads) => {
    try {
      const response = await leadsAPI.bulkCreateLeads(leads);
      const newLeads = response.data;
      // setLeads(prevLeads => [...prevLeads, ...newLeads]);
      return newLeads;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import leads');
      throw error;
    }
  };

  const updateLead = async (id, updatedLead) => {
    try {
      const response = await leadsAPI.updateLead(id, updatedLead);
      // setLeads(leads.map(lead => lead.id === id ? response.data : lead));
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update lead');
      throw error;
    }
  };

  const deleteLead = async (id) => {
    try {
      await leadsAPI.deleteLead(id);
      // setLeads(leads.filter(lead => lead.id !== id));
      toast.success('Lead deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete lead');
      throw error;
    }
  };

  // Call Management - Simplified for ElevenLabs integration
  const initiateCall = async (leadId) => {
    try {
      const response = await callsAPI.initiateCall(parseInt(leadId, 10));
      
      // Update lead status by invalidating the leads query
      queryClient.invalidateQueries(['leadsData']);
      queryClient.invalidateQueries(['callHistory']);

      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate call');
      throw error;
    }
  };

  const syncCallFromElevenLabs = async (callId, conversationId) => {
    try {
      const response = await callsAPI.syncElevenLabsCall({
        call_id: callId,
        conversation_id: conversationId
      });

      // Invalidate queries to refetch updated data
      queryClient.invalidateQueries(['leadsData']);
      queryClient.invalidateQueries(['callHistory']);

      toast.success('Call data synced successfully');
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync call data');
      throw error;
    }
  };

  const value = {
    addLead,
    addLeads,
    updateLead,
    deleteLead,
    initiateCall,
    syncCallFromElevenLabs
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext; 